import { useLocation, useNavigate } from "react-router-dom";
import StopsTable from "./StopsTable";
import NextStopTableScreen from "./NextStopVehicleScreen";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import config from "../../config.js";
import axios from "axios";
import {
  CircularProgress,
  Box,
  Button,
  Divider,
  Typography,
} from "@mui/material";
import NavMenu from "../../components/navbar/NavMenu";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";
import VehicleDetailsOverlay from "../../components/vehicle/VehicleDetailsOverlay";
import TripSegmentTracker from "./TripSegmentTracker.jsx";
import useCurrentTripStartStopLogs from "../../hooks/useCurrentTripStartStopLogs";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
} from "@mui/material";
import { reportsApi } from "../../components/manageData/reportsApi.js";
import { formatDate, formatTime, toDdMmYyyy } from "../../utils/formatDate.js";
import {
  getISTDateParts,
  getISTNowParts,
} from "../../utils/istDateUtils";

// Import sub-components
import VehicleHeader from "./vehicleTrack/VehicleHeader";
import {
  StopMarker,
  VehicleMarker,
  StoppedVehicleIndicator,
} from "./vehicleTrack/MapMarkers";
import RoutePolylines from "./vehicleTrack/RoutePolylines";
import { Polyline } from "@react-google-maps/api";
import PreviousTripsTable from "./PreviousTripsTable.jsx";
import MapLegend from "../../components/dashboard/MapLegend";
import RegularStopIcon from "../../images/driverConsole/regularMarker.svg";
import { getRightToLeftBusIcon } from "../stops/vehicleIcons.js";
import { filterStopsForDirection } from "./nextStopSelection";

// Import custom hooks
import { useVehicleTracking } from "./vehicleTrack/useVehicleTracking";
import { useOffRouteTrail } from "./vehicleTrack/useOffRouteTrail";
import {
  extractLiveTelemetry,
  mergeLiveTelemetry,
  applyMonitorReadings,
  formatSensorValue,
  getReadingColor,
  SENSOR_UNITS,
  smoothVehicleSpeed,
} from "./vehicleTrack/liveTelemetry";
import { getDeviceMonitorLatest } from "../../components/device/deviceApi";
import { useDirections } from "./vehicleTrack/useDirections";
import { usePathDirection } from "./vehicleTrack/usePathDirection";
import { useStopDetails } from "./vehicleTrack/useStopDetails";

// Import utilities
import {
  calculateDistance,
  findNearestPointOnRoute,
  detectVehicleMovement,
  extractPathPoints,
  splitRouteAtPosition,
  checkDirectionChange,
  isJourneyComplete as checkJourneyComplete,
} from "./vehicleTrack/mapUtils";

// Import history point utilities
import {
  saveHistoryPointsToLocalStorage,
  clearHistoryPointsFromLocalStorage,
} from "../../utils/historyPointUtils";

// Import label collision helpers
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets";

// Import constants
import {
  mapContainerStyle,
  mapStyles,
  vehicleInfoBoxStyle,
  mapOptions,
} from "./vehicleTrack/styles";
import {
  libraries,
  ACTUAL_PATH_COLOR,
  ACTUAL_PATH_STROKE_WEIGHT,
  ACTUAL_PATH_Z_INDEX,
} from "./vehicleTrack/mapConfig";

// Icons
import { useMapIcons } from "../../hooks/useMapIcons";
import { useSettings } from "../../context/SettingsContext";
import { useTranslation } from "react-i18next";

import AnalyticsTable from "./AnalyticsTable";
import StudentListTable from "./StudentListTable";

const DeviceLocationMap = ({ vehicleId, isSchoolMonitor = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryVehicleId = new URLSearchParams(location.search).get("vehicleId");
  const rawSelectedVehicleId = vehicleId || queryVehicleId || location.state;
  const selectedVehicleId =
    rawSelectedVehicleId && typeof rawSelectedVehicleId === "object"
      ? rawSelectedVehicleId.vehicleId || rawSelectedVehicleId.id
      : rawSelectedVehicleId;
  const NavigationMenu = isSchoolMonitor ? SchoolBusNavMenu : NavMenu;

  // ─── State ──────────────────────────────────────────────────────────────────
  const [position, setPosition] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [userState, setUserState] = useState(null);
  const [hasMapPermission, setHasMapPermission] = useState(false);
  const [speed, setSpeed] = useState(null);
  const [telemetry, setTelemetry] = useState({
    speed: null,
    temperature: null,
    humidity: null,
    vibration: null,
    smoke: null,
    voltage: null,
    satellites: null,
    ignition: null,
    ranges: {},
  });
  const [directions, setDirections] = useState(null);
  const [stopDetails, setStopDetails] = useState({});
  const [previousPosition, setPreviousPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [snappedPosition, setSnappedPosition] = useState(null);
  const [routePaths, setRoutePaths] = useState({ before: [], after: [] });
  const [isMoving, setIsMoving] = useState(true);
  const [lastMovedPosition, setLastMovedPosition] = useState(null);
  const [stoppedDuration, setStoppedDuration] = useState(0);
  const [positionHistory, setPositionHistory] = useState([]);
  const [fullPath, setFullPath] = useState([]);
  const [pathDirection, setPathDirection] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [historyPointInterval, setHistoryPointInterval] = useState(30000);
  const [historyPointDistanceInterval, setHistoryPointDistanceInterval] =
    useState(100);
  const [waypointMode, setWaypointMode] = useState("time");

  // ─── Waypoint tracking ──────────────────────────────────────────────────────
  const [waypoints, setWaypoints] = useState([]);
  const [selectedWaypoint, setSelectedWaypoint] = useState(null);
  const [showWaypointTrail, setShowWaypointTrail] = useState(true);
  const [waypointAddresses, setWaypointAddresses] = useState({});
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [movementLogs, setMovementLogs] = useState([]);
  const [tripGpsHistory, setTripGpsHistory] = useState(null);
  const [routeDeviationEvent, setRouteDeviationEvent] = useState(null);
  const [routeDeviation, setRouteDeviation] = useState(null);

  // ─── Historical trips ────────────────────────────────────────────────────────
  const [previousTrips, setPreviousTrips] = useState([]);
  const [selectedHistoricalTrip, setSelectedHistoricalTrip] = useState(null);
  const [historicalPath, setHistoricalPath] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingTripPath, setLoadingTripPath] = useState(false);
  const [completedTripsDate, setCompletedTripsDate] = useState(
    () => getISTNowParts().isoDate,
  );
  const [todayCompletedTripCount, setTodayCompletedTripCount] = useState(0);

  // ─── Bottom tab ──────────────────────────────────────────────────────────────
  const analyticsTabIndex = isSchoolMonitor ? 3 : 2;
  const [bottomTab, setBottomTab] = useState(
    location.state?.initialTab === "analytics" ? analyticsTabIndex : 0,
  );

  // ─── Icons ───────────────────────────────────────────────────────────────────
  const { settings } = useSettings();
  const {
    getVehicleIcon,
    getIntermediateStopIcon,
    getOriginStopIcon,
    getDestinationStopIcon,
  } = useMapIcons(
    settings,
    vehicle ? [vehicle] : [],
    stops,
    route ? [route] : [],
  );

  // ─── Refs ────────────────────────────────────────────────────────────────────
  const directionsRef = useRef(directions);
  const directionCheckRef = useRef(false);
  const lastWaypointTimeRef = useRef(null);
  const lastWaypointPositionRef = useRef(null);
  const fetchCurrentTripPathRef = useRef(null);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  // ─── Current trip start/stop logs (drives history point time window) ─────────
  const { logs: currentTripLogs } = useCurrentTripStartStopLogs(
    vehicle?.device_id,
    orgId,
    30000,
    !!vehicle?.device_id && !!orgId,
  );

  // Earliest timestamp from current trip logs = when this trip started
  const currentTripStartISO = React.useMemo(() => {
    if (!currentTripLogs || currentTripLogs.length === 0) return null;
    const earliest = currentTripLogs
      .map((l) => new Date(l.timestamp).getTime())
      .filter(Boolean)
      .sort((a, b) => a - b)[0];
    return earliest ? new Date(earliest).toISOString() : null;
  }, [currentTripLogs]);

  const hasInitialFitRef = useRef(false);

  // ─── Track last fetched location for movement detection ──────────────────────
  const lastFetchedLocationRef = useRef(null);
  const lastDataTimestampRef = useRef(null);
  const consecutiveStopCountRef = useRef(0);
  const STOP_THRESHOLD = 4; // 4-5 same coordinates = stop

  // ─── Fetch completed trips for the selected date ─────────────────────────────
  const fetchCompletedTrips = async (dateISO = completedTripsDate) => {
    if (!vehicle?.device_id || !dateISO) return [];
    try {
      setLoadingTrips(true);
      // Date-only "YYYY-MM-DD" is parsed as UTC midnight, and the report API
      // then treats the following day as inclusive. Query this IST calendar
      // day only, and never send an end instant that is still in the future.
      const dayStart = new Date(`${dateISO}T00:00:00+05:30`);
      const dayEnd = new Date(`${dateISO}T23:59:59.999+05:30`);
      const latest = new Date(Date.now() - 5000);
      if (Number.isNaN(dayStart.getTime()) || dayStart > latest) {
        setPreviousTrips([]);
        return [];
      }
      const rangeEnd = dayEnd > latest ? latest : dayEnd;

      const params = {
        orgId,
        siteId: null,
        category: "vehicles",
        selectedIds: [vehicle.device_id],
        selectAll: false,
        dateOption: "custom",
        customStartDate: dayStart.toISOString(),
        customEndDate: rangeEnd.toISOString(),
      };

      const response = await reportsApi.getTripReport(params);
      if (response?.data) {
        const sortedTrips = response.data.sort((a, b) => {
          // Convert any date format to a YYYY-MM-DD comparable string
          const toComparableDate = (dateStr) => {
            if (!dateStr) return "";
            const parts = dateStr.split("/");
            if (parts.length !== 3) return dateStr;
            // DD/MM/YYYY → YYYY-MM-DD
            if (parts[2]?.length === 4)
              return `${parts[2]}-${parts[1]}-${parts[0]}`;
            // YYYY/MM/DD → YYYY-MM-DD
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
          };

          // Compare as plain strings (YYYY-MM-DD HH:MM:SS) — no Date constructor,
          // no timezone ambiguity, identical result on local and deployed server.
          const aKey = `${toComparableDate(a.date)} ${a.startTime || "00:00:00"}`;
          const bKey = `${toComparableDate(b.date)} ${b.startTime || "00:00:00"}`;
          return bKey > aKey ? 1 : bKey < aKey ? -1 : 0; // descending: latest first
        });

        const mapped = sortedTrips.map((trip, idx) => ({
          ...trip,
          id: idx + 1,
        }));
        setPreviousTrips(mapped);
        return mapped; // ← return so callers can use without waiting for state update
      }
      return [];
    } catch (error) {
      console.error("Error fetching trips:", error);
      return [];
    } finally {
      setLoadingTrips(false);
    }
  };

  // ─── Fetch path for a selected historical trip ───────────────────────────────
  const fetchTripPath = async (trip) => {
    if (!trip || !vehicle?.device_id) return;
    try {
      setLoadingTripPath(true);
      setSelectedHistoricalTrip(trip);

      const formattedDate = toDdMmYyyy(trip.date);
      const to24HourTime = (timeStr) => {
        if (!timeStr) return "";
        const match = String(timeStr)
          .trim()
          .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
        if (!match) return String(timeStr).trim();
        let hours = parseInt(match[1], 10);
        const suffix = (match[4] || "").toUpperCase();
        if (suffix === "PM" && hours < 12) hours += 12;
        if (suffix === "AM" && hours === 12) hours = 0;
        return `${String(hours).padStart(2, "0")}:${match[2]}:${match[3] || "00"}`;
      };

      if (!formattedDate) {
        setHistoricalPath([]);
        return;
      }

      console.log("🔍 Fetching trip path:", {
        originalDate: trip.date,
        formattedDate,
        startTime: trip.startTime,
        endTime: trip.endTime,
        deviceId: vehicle.device_id,
      });

      const response = await axios.post(
        `${config.baseURL}/location/all`,
        { customDates: [] },
        {
          params: {
            deviceIds: vehicle.device_id,
            date: formattedDate,
            startTime: to24HourTime(trip.startTime),
            endTime: to24HourTime(trip.endTime),
            endDate: "",
            startDate: "",
            selectedDateRange: "today",
          },
          withCredentials: true,
        },
      );

      console.log(
        "✅ Trip path response:",
        response.data?.length || 0,
        "points",
      );

      if (response?.data && response.data.length > 0) {
        const sortedLocations = response.data
          .map((loc) => ({
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
            timestamp: new Date(loc.createdAt).getTime(),
          }))
          .filter(
            (point) =>
              Number.isFinite(point.lat) &&
              Number.isFinite(point.lng) &&
              Number.isFinite(point.timestamp),
          )
          .sort((a, b) => a.timestamp - b.timestamp);

        setHistoricalPath(sortedLocations);

        // if (map && sortedLocations.length > 0) {
        //   const bounds = new window.google.maps.LatLngBounds();
        //   sortedLocations.forEach((pt) =>
        //     bounds.extend({ lat: pt.lat, lng: pt.lng }),
        //   );
        //   map.fitBounds(bounds);
        // }
      } else {
        console.warn("⚠️ No location data returned for trip");
        setHistoricalPath([]);
      }
    } catch (error) {
      console.error("❌ Error fetching trip path:", error);
      setHistoricalPath([]);
    } finally {
      setLoadingTripPath(false);
    }
  };

  const fetchCurrentTripPath = async (currentTripStartISOOverride) => {
    if (!vehicle?.device_id || !route) return;

    // Use the override (passed on first call) or the live hook value
    const tripStartISO = currentTripStartISOOverride ?? currentTripStartISO;

    if (!tripStartISO) {
      console.log(
        "⏳ No current trip start time yet — skipping history point fetch",
      );
      return;
    }

    try {
      // ✅ IST-safe: getISTDateParts / getISTNowParts always return IST values
      // regardless of the server's system timezone (UTC on deployed Linux).
      const tripStartDate = new Date(tripStartISO);
      const { ddmmyyyy: tripDate, time: startTime } =
        getISTDateParts(tripStartDate);
      const { time: currentTime } = getISTNowParts();

      console.log(
        `📍 Fetching history points for current trip: ${tripDate} ${startTime} → ${currentTime}`,
      );

      const response = await axios.post(
        `${config.baseURL}/location/all`,
        { customDates: [] },
        {
          params: {
            deviceIds: vehicle.device_id,
            date: tripDate,
            startTime,
            endTime: currentTime,
            selectedDateRange: "today",
          },
          withCredentials: true,
        },
      );

      if (response?.data && response.data.length > 0) {
        // Filter strictly to points after tripStartISO, then sort by time
        const tripStartMs = tripStartDate.getTime();
        const filteredLocationData = response.data
          .filter((loc) => new Date(loc.createdAt).getTime() >= tripStartMs)
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );

        console.log(
          `✅ Current trip history: ${filteredLocationData.length} / ${response.data.length} points (after ${startTime})`,
        );

        // ── Haversine helper (metres) ─────────────────────────────────────────
        const haversineM = (lat1, lng1, lat2, lng2) => {
          const R = 6371000;
          const dLat = ((lat2 - lat1) * Math.PI) / 180;
          const dLng = ((lng2 - lng1) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLng / 2) ** 2;
          return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };

        // ── Thin by selected mode only (time XOR distance) ───────────────────
        const intervalMs = historyPointInterval || 30000;
        const distThresholdM = historyPointDistanceInterval || 100;
        const thinned = [];

        if (waypointMode === "distance") {
          for (const loc of filteredLocationData) {
            const lat = parseFloat(loc.latitude);
            const lng = parseFloat(loc.longitude);
            if (thinned.length === 0) {
              thinned.push(loc);
              continue;
            }
            const prev = thinned[thinned.length - 1];
            const distM = haversineM(
              parseFloat(prev.latitude),
              parseFloat(prev.longitude),
              lat,
              lng,
            );
            if (distM >= distThresholdM) {
              thinned.push(loc);
            }
          }
        } else {
          // time mode (default)
          let lastKeptTs = -Infinity;
          for (const loc of filteredLocationData) {
            const ts = new Date(loc.createdAt).getTime();
            if (ts - lastKeptTs >= intervalMs) {
              thinned.push(loc);
              lastKeptTs = ts;
            }
          }
        }

        // ── Deduplicate by position (vehicle stopped) ─────────────────────────
        const DEDUP_METERS = 20;
        const deduped = [];
        for (const loc of thinned) {
          const lat = parseFloat(loc.latitude);
          const lng = parseFloat(loc.longitude);
          if (deduped.length === 0) {
            deduped.push(loc);
            continue;
          }
          const prev = deduped[deduped.length - 1];
          const distM = haversineM(
            parseFloat(prev.latitude),
            parseFloat(prev.longitude),
            lat,
            lng,
          );
          if (distM > DEDUP_METERS) {
            deduped.push(loc);
          }
        }

        console.log(
          waypointMode === "distance"
            ? `🗺️ After dist-thin (${distThresholdM}m) + dedup: ${deduped.length} points`
            : `🗺️ After time-thin (${intervalMs / 1000}s) + dedup: ${deduped.length} points`,
        );

        const sortedWaypoints = deduped
          .map((loc) => ({
            id: `waypoint_${loc.id || loc.createdAt}`,
            latitude: parseFloat(loc.latitude),
            longitude: parseFloat(loc.longitude),
            speed: parseFloat(loc.Speed ?? loc.speed) || 0,
            timestamp: new Date(loc.createdAt).getTime(),
            formattedTime: formatTime(new Date(loc.createdAt)),
            formattedDate: formatDate(new Date(loc.createdAt)),
            vehicleId: vehicle.id || selectedVehicleId,
            vehicleName: vehicle?.name || "",
            vehicleNumber:
              vehicle?.VehicleNumber || vehicle?.vehicleNumber || "N/A",
          }))
          .sort((a, b) => a.timestamp - b.timestamp);

        // Replace (do not merge) so mode/interval changes do not leave stale dots
        if (vehicle?.device_id) {
          saveHistoryPointsToLocalStorage(sortedWaypoints, vehicle.device_id);
        }
        setWaypoints(sortedWaypoints);
        setTripGpsHistory(filteredLocationData);
      } else {
        console.warn("⚠️ No location history data for current trip");
        setTripGpsHistory([]);
      }
    } catch (error) {
      console.error("❌ Error fetching current trip path:", error);
      setTripGpsHistory((prev) => (prev == null ? [] : prev));
    }
  };

  // Keep the ref in sync so polling intervals always call the latest version
  fetchCurrentTripPathRef.current = fetchCurrentTripPath;

  // ─── Clear waypoints whenever the vehicle changes or a new trip starts ───────
  // This ensures we never show history points from a previous trip or day.
  // currentTripStartISO changing means the backend detected a new trip.
  useEffect(() => {
    if (!vehicle?.device_id) return;
    clearHistoryPointsFromLocalStorage(vehicle.device_id);
    setWaypoints([]);
    setTripGpsHistory(null);
    setRouteDeviationEvent(null);
    setRouteDeviation(null);
  }, [vehicle?.device_id, currentTripStartISO]);

  // ─── Sequenced effect: fetch history points only for the current trip ─────────
  // currentTripStartISO comes from useCurrentTripStartStopLogs — it is the
  // earliest timestamp in the current trip's START/STOP records, so we never
  // accidentally pull the full day's GPS history.
  useEffect(() => {
    if (!vehicle?.device_id || !route) return;

    let pollInterval = null;
    let cancelled = false;

    const init = async () => {
      if (cancelled) return;

      // Fetch GPS history for the current trip window
      await fetchCurrentTripPath();

      if (cancelled) return;

      // Poll every 30 s for new points.
      // Use the ref so the callback always calls the LATEST fetchCurrentTripPath
      // (avoids stale closure where currentTripStartISO is still null).
      pollInterval = setInterval(() => {
        if (!cancelled) fetchCurrentTripPathRef.current?.();
      }, 30 * 1000);
    };

    init();

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [vehicle?.device_id, route, currentTripStartISO]);

  useEffect(() => {
    if (!vehicle?.device_id) return undefined;
    let cancelled = false;
    const loadTrips = async () => {
      await fetchCompletedTrips(completedTripsDate);
      if (cancelled) return;
    };
    loadTrips();
    return () => {
      cancelled = true;
    };
  }, [vehicle?.device_id, completedTripsDate, orgId]);

  useEffect(() => {
    if (!vehicle?.device_id || !orgId) {
      setTodayCompletedTripCount(0);
      return undefined;
    }

    let cancelled = false;
    let requestId = 0;
    let pendingLowerCount = null;

    const countCompletedTrips = (rows) =>
      rows.filter((trip) => {
        const status = String(trip?.status || "").trim();
        if (!status || status === "N/A" || status.startsWith("In Progress")) {
          return false;
        }
        return true;
      }).length;

    const loadTodayCompletedTrips = async () => {
      const thisRequest = ++requestId;
      try {
        const response = await reportsApi.getTripReport({
          orgId,
          siteId: null,
          category: "vehicles",
          selectedIds: [vehicle.device_id],
          selectAll: false,
          dateOption: "today",
        });
        if (cancelled || thisRequest !== requestId) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const completedCount = countCompletedTrips(rows);
        setTodayCompletedTripCount((previous) => {
          if (completedCount >= previous) {
            pendingLowerCount = null;
            return completedCount;
          }
          // Ignore a one-off lower count (partial / overlapping report).
          if (pendingLowerCount === completedCount) {
            pendingLowerCount = null;
            return completedCount;
          }
          pendingLowerCount = completedCount;
          return previous;
        });
      } catch (error) {
        console.error("Error fetching today's completed trips:", error);
      }
    };

    loadTodayCompletedTrips();
    const intervalId = setInterval(loadTodayCompletedTrips, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [vehicle?.device_id, orgId]);

  // Recompute trail when user switches Time ↔ Distance
  useEffect(() => {
    if (!vehicle?.device_id || !route || !currentTripStartISO) return;
    setWaypoints([]);
    setSelectedWaypoint(null);
    fetchCurrentTripPathRef.current?.();
  }, [waypointMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map || !fullPath?.length || hasInitialFitRef.current) return undefined;

    const fitRoute = () => {
      const container = map.getDiv?.();
      if (container && container.clientHeight < 80) return;
      window.google.maps.event.trigger(map, "resize");
      const bounds = new window.google.maps.LatLngBounds();
      fullPath.forEach((point) => {
        bounds.extend({ lat: point.lat, lng: point.lng });
      });
      map.fitBounds(bounds, { padding: 60 });
      hasInitialFitRef.current = true;
    };

    const frame = window.requestAnimationFrame(fitRoute);
    const container = map.getDiv?.();
    const observer = container ? new ResizeObserver(fitRoute) : null;
    if (container) observer.observe(container);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [map, fullPath]);

  const handleClearSelectedTrip = () => {
    setSelectedHistoricalTrip(null);
    setHistoricalPath([]);
    if (map && position) {
      map.panTo(position);
      map.setZoom(16);
    }
  };

  // ─── Address lookup (Nominatim) ──────────────────────────────────────────────
  const getAddressFromCoordinates = async (lat, lng) => {
    const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (waypointAddresses[cacheKey]) return waypointAddresses[cacheKey];
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            "User-Agent":
              "RouteEyeVehicleTracking/1.0 (Vehicle Tracking Application)",
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch address");
      const data = await response.json();

      let address = "";
      if (data.address) {
        const parts = [];
        if (data.address.road) parts.push(data.address.road);
        if (data.address.neighbourhood) parts.push(data.address.neighbourhood);
        else if (data.address.suburb) parts.push(data.address.suburb);
        if (data.address.city) parts.push(data.address.city);
        else if (data.address.town) parts.push(data.address.town);
        else if (data.address.village) parts.push(data.address.village);
        if (data.address.state) parts.push(data.address.state);
        address = parts.join(", ");
      }
      if (!address && data.display_name)
        address = data.display_name.split(",").slice(0, 4).join(",");
      if (!address) address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

      setWaypointAddresses((prev) => ({ ...prev, [cacheKey]: address }));
      return address;
    } catch (error) {
      console.error("Error fetching address:", error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  // ─── Haversine helper ────────────────────────────────────────────────────────
  const calculateDistanceInMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // ─── Fetch address when waypoint selected ────────────────────────────────────
  useEffect(() => {
    const fetchWaypointAddress = async () => {
      if (selectedWaypoint) {
        const cacheKey = `${selectedWaypoint.latitude.toFixed(6)},${selectedWaypoint.longitude.toFixed(6)}`;
        if (!waypointAddresses[cacheKey]) {
          setLoadingAddress(true);
          await getAddressFromCoordinates(
            selectedWaypoint.latitude,
            selectedWaypoint.longitude,
          );
          setLoadingAddress(false);
        }
      }
    };
    fetchWaypointAddress();
  }, [selectedWaypoint]);

  // ─── Fetch app settings ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAppSettings = async () => {
      try {
        const response = await axios.get(
          `${config.baseURL}/appSettings/organization`,
          { params: { organizationId: orgId }, withCredentials: true },
        );
        if (response.data?.historyPointInterval) {
          const intervalMs = response.data.historyPointInterval * 1000;
          setHistoryPointInterval(intervalMs);
          console.log("✅ Fetched historyPointInterval from API:", intervalMs);
        } else {
          const local = JSON.parse(localStorage.getItem("appSettings"));
          setHistoryPointInterval((local?.historyPointInterval || 30) * 1000);
          console.log("⚠️ Using historyPointInterval from localStorage");
        }
        const apiDistInterval =
          response.data?.historyDistanceInterval ??
          response.data?.historyPointDistanceInterval;
        if (apiDistInterval != null && apiDistInterval !== "") {
          setHistoryPointDistanceInterval(Number(apiDistInterval));
          console.log(
            "✅ Fetched historyDistanceInterval from API:",
            apiDistInterval,
            "m",
          );
        } else {
          const local = JSON.parse(localStorage.getItem("appSettings"));
          setHistoryPointDistanceInterval(
            Number(
              local?.historyDistanceInterval ??
                local?.historyPointDistanceInterval ??
                100,
            ),
          );
          console.log(
            "⚠️ Using historyDistanceInterval from localStorage",
          );
        }
      } catch (error) {
        console.error("❌ Error fetching app settings:", error);
        const local = JSON.parse(localStorage.getItem("appSettings"));
        setHistoryPointInterval((local?.historyPointInterval || 30) * 1000);
        setHistoryPointDistanceInterval(
          Number(
            local?.historyDistanceInterval ??
              local?.historyPointDistanceInterval ??
              100,
          ),
        );
        console.log("⚠️ Fallback to localStorage due to API error");
      }
    };
    if (orgId) fetchAppSettings();
  }, [orgId]);

  // ─── Direction is for route coloring / stop order only ───────────────────────
  // Do NOT clear history waypoints when pathDirection is first detected or restored.
  // That used to wipe the trail shortly after load; trip boundaries already clear
  // via currentTripStartISO above. Only clear when direction actually reverses
  // (source_to_dest ↔ dest_to_source), then refetch the current-trip trail.
  const prevPathDirectionRef = useRef(null);
  useEffect(() => {
    const prev = prevPathDirectionRef.current;
    prevPathDirectionRef.current = pathDirection;

    if (!vehicle?.device_id) return;
    // Initial set (null → direction) or clear (direction → null): keep trail
    if (!prev || !pathDirection) return;
    if (prev === pathDirection) return;

    console.log(
      `🧭 Path direction reversed ${prev} → ${pathDirection}; refreshing trail`,
    );
    setWaypoints([]);
    setMovementLogs([]);
    setSelectedWaypoint(null);
    setWaypointAddresses({});
    lastWaypointTimeRef.current = null;
    lastWaypointPositionRef.current = null;
    clearHistoryPointsFromLocalStorage(vehicle.device_id);
    fetchCurrentTripPathRef.current?.();
  }, [pathDirection, vehicle?.device_id]);

  // ─── Global clear callback for TripSegmentTracker ────────────────────────────
  useEffect(() => {
    window.clearWaypointsForTrip = () => {
      console.log("🗑️ Clearing waypoints via global callback");
      setWaypoints([]);
      setMovementLogs([]);
      setSelectedWaypoint(null);
      setWaypointAddresses({});
      lastWaypointTimeRef.current = null;
      lastWaypointPositionRef.current = null;
    };
    return () => {
      delete window.clearWaypointsForTrip;
    };
  }, []);

  useEffect(() => {
    directionsRef.current = directions;
  }, [directions]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const onLoad = useCallback((map) => setMap(map), []);
  const onUnmount = useCallback(() => setMap(null), []);

  // ─── User permissions ────────────────────────────────────────────────────────
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userState"));
    if (user) {
      setUserState(user);
      setHasMapPermission(
        user.features.some(
          (f) => f.api_path === "/api/stop/organization" && f.action === "GET",
        ),
      );
    }
  }, []);

  // ─── Fetch vehicle + route + stops ──────────────────────────────────────────
  const fetchVehicleData = async () => {
    if (!selectedVehicleId) return;
    try {
      const response = await axios.get(`${config.baseURL}/vehicle/id`, {
        params: { vehicleId: selectedVehicleId, organizationId: orgId },
        withCredentials: true,
      });
      const vehicleData = response.data.vehicle;
      console.log("Fetched vehicle data:", vehicleData);
      setVehicle(vehicleData);
      if (vehicleData?.device_id) {
        try {
          const latest = await axios.get(
            `${config.baseURL}/location/latest/${vehicleData.device_id}`,
            { withCredentials: true },
          );
          const seeded = extractLiveTelemetry(latest.data || {});
          const { speed: seedSpeed, ...seedTelemetry } = seeded;
          setTelemetry((previous) => mergeLiveTelemetry(previous, seedTelemetry));
          if (seedSpeed != null) {
            setTelemetry((previous) =>
              mergeLiveTelemetry(previous, {
                speed: smoothVehicleSpeed(previous.speed, seedSpeed),
              }),
            );
            setSpeed((previous) => smoothVehicleSpeed(previous, seedSpeed));
          }
        } catch (latestError) {
          console.error("Error fetching latest location telemetry:", latestError);
        }
      }
      if (vehicleData?.id) {
        try {
          const monitor = await getDeviceMonitorLatest({
            vehicleId: vehicleData.id,
            organizationId: orgId,
          });
          const fromSensors = applyMonitorReadings(monitor?.data || []);
          const { sensors: _sensors, speed: _sensorSpeed, ...sensorTelemetry } =
            fromSensors;
          setTelemetry((previous) => mergeLiveTelemetry(previous, sensorTelemetry));
        } catch (monitorError) {
          console.error("Error fetching vehicle sensor readings:", monitorError);
        }
      }

      const routeData = await axios.get(`${config.baseURL}/route/id`, {
        params: { id: vehicleData.routeId },
        withCredentials: true,
      });
      if (routeData.data) {
        setRoute(routeData.data);
        setStops(await fetchStopsForRoute(routeData.data));
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
    }
  };

  const fetchStopsForRoute = async (route) => {
    if (!route?.routeCoordinates) return [];
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const response = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: orgId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          },
        );
        stops.push({
          id: response.data.id,
          stopId: response.data.stopId || response.data.id,
          name: response.data.name,
          latitude: response.data.latitude,
          longitude: response.data.longitude,
        });
      } catch (error) {
        console.error("Error fetching stops:", error);
      }
    }
    return stops;
  };

  useEffect(() => {
    fetchVehicleData();
    const intervalId = setInterval(fetchVehicleData, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [selectedVehicleId, orgId]);

  useEffect(() => {
    if (!vehicle?.device_id) return undefined;

    const loadLatestTelemetry = async () => {
      try {
        const latest = await axios.get(
          `${config.baseURL}/location/latest/${vehicle.device_id}`,
          { withCredentials: true },
        );
        const seeded = extractLiveTelemetry(latest.data || {});
        const { speed: pollSpeed, ...pollTelemetry } = seeded;
        setTelemetry((previous) => {
          if (previous.speed != null || pollSpeed == null) {
            return mergeLiveTelemetry(previous, pollTelemetry);
          }
          return mergeLiveTelemetry(previous, {
            ...pollTelemetry,
            speed: pollSpeed,
          });
        });
        if (pollSpeed != null) {
          setSpeed((previous) => (previous == null ? pollSpeed : previous));
        }
      } catch (latestError) {
        console.error("Error polling latest location telemetry:", latestError);
      }
    };

    loadLatestTelemetry();
    const intervalId = setInterval(loadLatestTelemetry, 15000);
    return () => clearInterval(intervalId);
  }, [vehicle?.device_id]);

  useEffect(() => {
    if (!vehicle?.id) return undefined;

    const loadSensorReadings = async () => {
      try {
        const monitor = await getDeviceMonitorLatest({
          vehicleId: vehicle.id,
          organizationId: orgId,
        });
        const fromSensors = applyMonitorReadings(monitor?.data || []);
        const { sensors: _sensors, speed: _sensorSpeed, ...sensorTelemetry } =
          fromSensors;
        setTelemetry((previous) => mergeLiveTelemetry(previous, sensorTelemetry));
      } catch (monitorError) {
        console.error("Error polling vehicle sensor readings:", monitorError);
      }
    };

    loadSensorReadings();
    const intervalId = setInterval(loadSensorReadings, 15000);
    return () => clearInterval(intervalId);
  }, [vehicle?.id, orgId]);

  // ─── Visible stops (direction-aware) ────────────────────────────────────────
  const visibleStops = useMemo(
    () => filterStopsForDirection(stops, pathDirection),
    [stops, pathDirection],
  );

  // ─── Label collision offsets ──────────────────────────────────────────────────
  // Stop labels: push overlapping labels upward so they don't pile up.
  const stopLabelOffsets = useMemo(() => {
    if (!map || !visibleStops.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = visibleStops.map((s) => ({
      id: s.id,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
      name: s.name,
    }));
    return computeStopLabelOffsets(projection, map.getZoom() ?? 16, entries, {
      BASE_OFFSET: 44,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, visibleStops, zoom]);

  // Vehicle label: single vehicle but still collision-aware (handles future multi-vehicle extension).
  const vehicleLabelOffset = useMemo(() => {
    if (!map || !position) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = [
      {
        deviceId: vehicle?.device_id ?? "vehicle",
        lat: position.lat,
        lng: position.lng,
        name: vehicle?.name || "Vehicle",
        hasArrow: false,
      },
    ];
    return computeVehicleLabelOffsets(
      projection,
      map.getZoom() ?? 16,
      entries,
      {
        ANCHOR_SHIFT: 13,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, position, zoom]);

  // ─── Waypoint recording (time + distance modes) ──────────────────────────────
  // useEffect(() => {
  //   if (!position || !isMoving || !vehicle) return;

  //   const now = Date.now();
  //   let shouldRecord = false;

  //   if (waypointMode === "time") {
  //     if (
  //       !lastWaypointTimeRef.current ||
  //       now - lastWaypointTimeRef.current >= historyPointInterval
  //     ) {
  //       shouldRecord = true;
  //     }
  //   } else if (waypointMode === "distance") {
  //     if (!lastWaypointPositionRef.current) {
  //       shouldRecord = true;
  //     } else {
  //       const dist = calculateDistanceInMeters(
  //         lastWaypointPositionRef.current.lat,
  //         lastWaypointPositionRef.current.lng,
  //         position.lat,
  //         position.lng,
  //       );
  //       if (dist >= DISTANCE_THRESHOLD) shouldRecord = true;
  //     }
  //   }

  //   if (shouldRecord) {
  //     const waypoint = {
  //       id: `waypoint_${now}`,
  //       latitude: position.lat,
  //       longitude: position.lng,
  //       speed: parseFloat(speed) || 0,
  //       timestamp: now,
  //       formattedTime: formatTime(new Date(now)),
  //       formattedDate: formatDate(new Date(now)),
  //       vehicleId: vehicle.id || location.state,
  //       vehicleNumber:
  //         vehicle?.VehicleNumber || vehicle?.vehicleNumber || "N/A",
  //     };

  //     setWaypoints((prev) => [...prev, waypoint]);

  //     lastWaypointTimeRef.current = now;
  //     lastWaypointPositionRef.current = {
  //       lat: position.lat,
  //       lng: position.lng,
  //     };
  //     console.log(
  //       `📍 Waypoint recorded (${waypointMode} mode):`,
  //       waypoint,
  //       "Speed:",
  //       speed,
  //     );
  //   }
  // }, [
  //   position,
  //   isMoving,
  //   speed,
  //   vehicle,
  //   location.state,
  //   historyPointInterval,
  //   waypointMode,
  // ]);

  // ─── Custom hooks ────────────────────────────────────────────────────────────
  useVehicleTracking({
    vehicle,
    position,
    heading,
    lastMovedPosition,
    findNearestPointOnRoute,
    detectVehicleMovement,
    calculateDistance,
    directionsRef,
    map,
    setPosition,
    setHeading,
    setSpeed,
    setIsMoving,
    setLastMovedPosition,
    setStoppedDuration,
    setSnappedPosition,
    setPreviousPosition,
    setPositionHistory,
    setIsOnline,
    setIsReconnecting,
    setTelemetry,
    setRouteDeviationEvent,
    setRouteDeviation,
  });

  const { offRouteSegments } = useOffRouteTrail({
    deviceId: vehicle?.device_id,
    tripStartISO: currentTripStartISO,
    fullPath,
    position,
    routeDeviationEvent,
    routeDeviation,
    tripGpsHistory,
  });

  const roadOffRouteSegments = useMemo(() => {
    const segments = offRouteSegments
      .map((segment) =>
        [...segment]
          .filter(
            (point) =>
              Number.isFinite(point?.lat) && Number.isFinite(point?.lng),
          )
          .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)),
      )
      .filter((segment) => segment.length > 1);

    if (
      !position ||
      !Number.isFinite(position.lat) ||
      !Number.isFinite(position.lng) ||
      segments.length === 0
    ) {
      return segments;
    }

    const current = {
      lat: position.lat,
      lng: position.lng,
      timestamp: position.timestamp || Date.now(),
    };
    const last = segments[segments.length - 1];
    const end = last[last.length - 1];
    if (calculateDistance(end, current) < 8) return segments;
    return [...segments.slice(0, -1), [...last, current]];
  }, [
    offRouteSegments,
    position,
    calculateDistance,
  ]);

  useDirections({
    isLoaded,
    stops,
    route,
    position,
    map,
    setDirections,
    setFullPath,
    extractPathPoints,
  });

  usePathDirection({
    vehicle,
    positionHistory,
    pathDirection,
    stops,
    calculateDistance,
    checkDirectionChange,
    checkJourneyComplete,
    setPathDirection,
    setPositionHistory,
    directionCheckRef,
  });

  useStopDetails({ stops: visibleStops, vehicle, setStopDetails });

  // ─── Split route at snapped position ─────────────────────────────────────────
  useEffect(() => {
    if (directions && snappedPosition) {
      const allPoints = extractPathPoints(directions);
      setRoutePaths(
        splitRouteAtPosition(
          allPoints,
          snappedPosition,
          pathDirection,
          calculateDistance,
        ),
      );
    }
  }, [directions, snappedPosition, pathDirection]);

  // ─── Navigation ──────────────────────────────────────────────────────────────
  const handleVehicleView = () =>
    navigate(`/vehicle/screen/${selectedVehicleId}`, {
      state: { vehicleId: selectedVehicleId, organizationId: orgId },
    });

  const handleDriverView = () =>
    navigate({
      pathname: "/console",
      search: `?routeId=${vehicle?.routeId}&organizationId=${orgId}&vehicleId=${selectedVehicleId}`,
    });

  const handleClearWaypoints = () => {
    setWaypoints([]);
    setSelectedWaypoint(null);
    setWaypointAddresses({});
    lastWaypointTimeRef.current = null;
    lastWaypointPositionRef.current = null;
  };

  const formatPersonName = (person) => {
    if (!person) return "";

    return [
      person.firstName || person.first_name,
      person.lastName === "." ? "" : person.lastName || person.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  };

  const driverName =
    formatPersonName(vehicle?.driver) ||
    vehicle?.driverName ||
    vehicle?.driver_name ||
    t("N/A");
  const driverPhone =
    vehicle?.driver?.phoneNumber ||
    vehicle?.driver?.phone_number ||
    vehicle?.driver?.contactPhone ||
    vehicle?.driver?.phone ||
    "";
  const attendantName =
    formatPersonName(vehicle?.employee) ||
    formatPersonName(vehicle?.attendant) ||
    vehicle?.attendantName ||
    vehicle?.employeeName ||
    t("N/A");
  const speedLimit = Number(
    vehicle?.speed_limit || vehicle?.speedLimit || settings?.maxSpeed
  );
  const temperatureRange = telemetry.ranges?.temperature || {};
  const vibrationRange = telemetry.ranges?.vibration || {};
  const smokeRange = telemetry.ranges?.smoke || {};
  const voltageRange = telemetry.ranges?.voltage || {};
  const speedValue = telemetry.speed ?? speed;
  const isOverSpeed =
    speedValue !== null &&
    Number.isFinite(speedLimit) &&
    speedLimit > 0 &&
    speedValue > speedLimit;
  const speedColor = isOverSpeed
    ? "#EE3B2B"
    : speedValue !== null
      ? "#008C23"
      : "#050C1C";

  const vehicleLegendIconUrl = useMemo(() => {
    if (isLoaded && vehicle?.device_id) {
      const iconObj = getVehicleIcon(vehicle.device_id, 270);
      if (iconObj?.url) return iconObj.url;
    }
    const svg = getRightToLeftBusIcon(vehicle?.vehicle_color || "#00a896", 270).trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }, [isLoaded, vehicle, getVehicleIcon]);

  const stopLegendItems = useMemo(() => {
    if (!isLoaded) {
      return [{ label: "Stop", iconUrl: RegularStopIcon }];
    }
    const stopIcon = getIntermediateStopIcon();
    return [{ label: "Stop", iconUrl: stopIcon?.url || RegularStopIcon }];
  }, [isLoaded, getIntermediateStopIcon]);

  const hasTelemetryReading = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number !== 0;
  };

  const formatRangeHint = (range, unit) => {
    if (range?.min == null && range?.max == null) return "";
    const min = range.min ?? "---";
    const max = range.max ?? "---";
    return `${t("Allowed Range")}: ${min}${unit} - ${max}${unit}`;
  };

  const summaryItems = [
    {
      label: t("Reg No."),
      value: vehicle?.VehicleNumber || vehicle?.vehicleNumber || t("N/A"),
    },
    {
      label: t("Driver"),
      value: driverName,
      detail: driverPhone,
    },
    {
      label: t("Current Speed"),
      value: formatSensorValue(speedValue, SENSOR_UNITS.speed),
      hint: Number.isFinite(speedLimit)
        ? `${t("Speed limit")}: ${speedLimit} ${SENSOR_UNITS.speed}`
        : "",
      color: speedColor,
    },
    {
      label: t("Completed trips today"),
      value: todayCompletedTripCount,
    },
    isSchoolMonitor && { label: t("Attendant"), value: attendantName },
    hasTelemetryReading(telemetry.vibration) && {
      label: t("Vibration"),
      value: formatSensorValue(
        telemetry.vibration,
        vibrationRange.unit || SENSOR_UNITS.vibration,
      ),
      hint: formatRangeHint(
        vibrationRange,
        vibrationRange.unit ? ` ${vibrationRange.unit}` : ` ${SENSOR_UNITS.vibration}`,
      ),
      color: getReadingColor(
        telemetry.vibration,
        vibrationRange.min,
        vibrationRange.max,
      ),
    },
    hasTelemetryReading(telemetry.temperature) && {
      label: t("Temperature"),
      value: formatSensorValue(
        telemetry.temperature,
        temperatureRange.unit || SENSOR_UNITS.temperature,
      ),
      hint: formatRangeHint(
        temperatureRange,
        temperatureRange.unit || SENSOR_UNITS.temperature,
      ),
      color: getReadingColor(
        telemetry.temperature,
        temperatureRange.min,
        temperatureRange.max,
      ),
    },
    hasTelemetryReading(telemetry.humidity) && {
      label: t("Humidity"),
      value: formatSensorValue(
        telemetry.humidity,
        telemetry.ranges?.humidity?.unit || SENSOR_UNITS.humidity,
      ),
      hint: formatRangeHint(
        telemetry.ranges?.humidity || {},
        telemetry.ranges?.humidity?.unit || SENSOR_UNITS.humidity,
      ),
      color: getReadingColor(
        telemetry.humidity,
        telemetry.ranges?.humidity?.min,
        telemetry.ranges?.humidity?.max,
      ),
    },
    hasTelemetryReading(telemetry.voltage) && {
      label: t("Voltage"),
      value: formatSensorValue(
        telemetry.voltage,
        voltageRange.unit || SENSOR_UNITS.voltage,
      ),
      hint: formatRangeHint(
        voltageRange,
        voltageRange.unit ? ` ${voltageRange.unit}` : ` ${SENSOR_UNITS.voltage}`,
      ),
      color: getReadingColor(
        telemetry.voltage,
        voltageRange.min,
        voltageRange.max,
      ),
    },
    hasTelemetryReading(telemetry.smoke) && {
      label: t("Smoke"),
      value: formatSensorValue(
        telemetry.smoke,
        smokeRange.unit || SENSOR_UNITS.smoke,
      ),
      hint: formatRangeHint(
        smokeRange,
        smokeRange.unit ? ` ${smokeRange.unit}` : ` ${SENSOR_UNITS.smoke}`,
      ),
      color: getReadingColor(telemetry.smoke, smokeRange.min, smokeRange.max),
    },
  ].filter(Boolean);
  const isTrackingDataLoading = !vehicle || !route || stops == null;

  console.log(selectedWaypoint);

  if (loadError) {
    return (
      <div>
        <TitleBar />
        <NavigationMenu />
        <div style={{ padding: "32px" }}>
          Error loading maps. Please try again later.
        </div>
      </div>
    );
  }

  const orgName = parsedData?.organization?.name;

  return (
    <>
      {isSchoolMonitor ? (
        <></>
      ) : (
        <>
          <TitleBar />
          <NavigationMenu />
        </>
      )}
      <MonitorSubtitle
        title={isSchoolMonitor ? "Monitor School Bus" : "Monitor Vehicle"}
        path={`${orgName || "organization"} > ${
          isSchoolMonitor ? "School Vehicles" : "Vehicles"
        } > ${vehicle?.name || "Monitor Vehicle"} > Monitor`}
        track={isSchoolMonitor ? null : "Vehicle View"}
        add={isSchoolMonitor ? null : "Driver View"}
        onTrackClick={isSchoolMonitor ? undefined : handleVehicleView}
        onAddClick={isSchoolMonitor ? undefined : handleDriverView}
      />
      <Box
        sx={{
          borderRadius: "10px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          padding: { xs: "18px", sm: "16px 20px", md: "20px 24px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "0px 15px", sm: "0px 20px", md: "0px 40px" },
          boxShadow: "-2px 1px 5px 1px #00000040",
          gap: { xs: 2, sm: 0 },
          overflowX: { xs: "hidden", sm: "auto" },
        }}
      >
        {summaryItems.map((item, index) => (
          <React.Fragment key={item.label}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: { xs: "16px", md: "20px" },
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  textAlign: "center",
                  color: "#183059",
                  mb: 1.2,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 700,
                  fontSize: { xs: "20px", md: "24px" },
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  textAlign: "center",
                  color: item.color || "#050C1C",
                  wordBreak: "break-word",
                }}
              >
                {isTrackingDataLoading ? (
                  <CircularProgress size={22} sx={{ color: "#20A8E0" }} />
                ) : (
                  item.value
                )}
              </Typography>
              {item.detail && !isTrackingDataLoading && (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: { xs: "13px", md: "16px" },
                    color: "#647490",
                    textAlign: "center",
                  }}
                >
                  {item.detail}
                </Typography>
              )}
              {item.hint && !isTrackingDataLoading && (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: { xs: "12px", md: "14px" },
                    color: "#647490",
                    textAlign: "center",
                  }}
                >
                  {item.hint}
                </Typography>
              )}
            </Box>
            {index < summaryItems.length - 1 && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", sm: "block" },
                  mx: { sm: 2.5, md: 4 },
                  alignSelf: "stretch",
                  borderColor: "#183059",
                  borderRightWidth: "2px",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
      <div
        style={{
          padding: "0px 40px",
          width: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {hasMapPermission && (
          <>
            {!isLoaded ? (
              <div
                style={{
                  height: "68.5vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  alignItems: "stretch",
                  marginBottom: "20px",
                  width: "100%",
                  boxSizing: "border-box",
                  position: "relative",
                  zIndex: 0,
                }}
              >
                <Box
                  sx={{
                    ...vehicleInfoBoxStyle,
                    position: "relative",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <VehicleHeader vehicle={vehicle} route={route} />

                  <div
                    style={{
                      display: "flex",
                      minWidth: 0,
                      overflow: "hidden",
                      gap: "20px",
                      marginTop: "20px",
                    }}
                  >
                    {/* ── Main Map ── */}
                    <div
                      style={{
                        position: "relative",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Trail toggle + mode selector */}
                      <div
                        style={{
                          position: "absolute",
                          top: "30px",
                          left: "10px",
                          zIndex: 100,
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            setShowWaypointTrail(!showWaypointTrail)
                          }
                          sx={{
                            backgroundColor: "#1e3a5f",
                            color: "white",
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 500,
                            padding: "6px 14px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                            "&:hover": { backgroundColor: "#2c5282" },
                          }}
                        >
                          {showWaypointTrail
                            ? t("Hide Vehicle Trail")
                            : t("Show Vehicle Trail")}
                        </Button>

                        <FormControl
                          size="small"
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "4px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                          }}
                        >
                          <Select
                            value={waypointMode}
                            onChange={(e) => setWaypointMode(e.target.value)}
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                { border: "none" },
                            }}
                          >
                            <MenuItem value="time" sx={{ fontSize: "13px" }}>
                              Time Based
                            </MenuItem>
                            <MenuItem
                              value="distance"
                              sx={{ fontSize: "13px" }}
                            >
                              Distance Based
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </div>

                      <GoogleMap
                        mapContainerStyle={{
                          ...mapContainerStyle,
                          width: "100%",
                          height: "68.5vh",
                        }}
                        defaultCenter={
                          position ||
                          (stops?.length
                            ? {
                                lat: parseFloat(stops[0].latitude),
                                lng: parseFloat(stops[0].longitude),
                              }
                            : { lat: 12.9716, lng: 77.5946 })
                        }
                        defaultZoom={14}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{ ...mapOptions, minZoom: 10, styles: mapStyles }}
                        onZoomChanged={() => {
                          if (map) setZoom(map.getZoom());
                        }}
                      >
                        <RoutePolylines
                          fullPath={fullPath}
                          pathDirection={pathDirection}
                          routePaths={routePaths}
                          routeColor={route?.routeColor}
                        />

                        {roadOffRouteSegments.map((segment, index) => (
                          <Polyline
                            key={`off-route-${index}-${segment.length}`}
                            path={segment}
                            options={{
                              strokeColor: ACTUAL_PATH_COLOR,
                              strokeOpacity: 0.95,
                              strokeWeight: ACTUAL_PATH_STROKE_WEIGHT,
                              zIndex: ACTUAL_PATH_Z_INDEX,
                              geodesic: true,
                            }}
                          />
                        ))}

                        <StoppedVehicleIndicator
                          position={position}
                          stoppedDuration={stoppedDuration}
                          isMoving={isMoving}
                        />

                        {visibleStops.map((stop, index) => (
                          <StopMarker
                            key={`stop-${stop.id}-${index}`}
                            stop={stop}
                            index={index}
                            stopsLength={visibleStops.length}
                            getOriginIcon={getOriginStopIcon}
                            getDestinationIcon={getDestinationStopIcon}
                            getIntermediateStopIcon={getIntermediateStopIcon}
                            route={route ? [route] : []}
                            labelOffsetPx={
                              stopLabelOffsets[stop.id]?.topPx ?? 44
                            }
                          />
                        ))}

                        {/* Historical trip path */}
                        {/* {historicalPath.length > 0 && (
                          <>
                            <Polyline
                              path={historicalPath}
                              options={{
                                strokeColor: "#FF6B00",
                                strokeOpacity: 0.8,
                                strokeWeight: 5,
                                zIndex: 1000,
                              }}
                            />
                            <Marker
                              position={historicalPath[0]}
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: "#00FF00",
                                fillOpacity: 1,
                                strokeColor: "#FFFFFF",
                                strokeWeight: 2,
                              }}
                              title="Trip Start"
                            />
                            <Marker
                              position={
                                historicalPath[historicalPath.length - 1]
                              }
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: "#FF0000",
                                fillOpacity: 1,
                                strokeColor: "#FFFFFF",
                                strokeWeight: 2,
                              }}
                              title="Trip End"
                            />
                          </>
                        )} */}

                        {/* Live waypoint trail */}
                        {showWaypointTrail &&
                          waypoints.map((waypoint) => (
                            <Marker
                              key={waypoint.id}
                              position={{
                                lat: parseFloat(waypoint.latitude),
                                lng: parseFloat(waypoint.longitude),
                              }}
                              onClick={() => setSelectedWaypoint(waypoint)}
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 5,
                                fillColor: "#4285F4",
                                fillOpacity: 0.9,
                                strokeColor: "#FFFFFF",
                                strokeWeight: 2,
                              }}
                            />
                          ))}

                        {/* Waypoint InfoWindow */}
                        {showWaypointTrail && selectedWaypoint && (
                          <InfoWindow
                            position={{
                              lat: parseFloat(selectedWaypoint.latitude),
                              lng: parseFloat(selectedWaypoint.longitude),
                            }}
                            onCloseClick={() => setSelectedWaypoint(null)}
                            options={{
                              pixelOffset: new window.google.maps.Size(0, -10),
                            }}
                          >
                            <div
                              style={{
                                padding: "0",
                                minWidth: "280px",
                                fontFamily:
                                  "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                              }}
                            >
                              <div
                                style={{
                                  borderBottom: "1px solid #e0e0e0",
                                  paddingBottom: "8px",
                                  marginBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#666",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {selectedWaypoint.formattedTime}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#1a73e8",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {selectedWaypoint.vehicleName
                                    ? selectedWaypoint.vehicleName
                                    : selectedWaypoint.vehicleNumber
                                    ? `Vehicle-${selectedWaypoint.vehicleId} (${selectedWaypoint.vehicleNumber})`
                                    : `Vehicle-${selectedWaypoint.vehicleId}`}
                                </div>
                                <div
                                  style={{ fontSize: "12px", color: "#666" }}
                                >
                                  Date: {selectedWaypoint.formattedDate}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: "#34a853",
                                  color: "white",
                                  padding: "8px 12px",
                                  borderRadius: "4px",
                                  textAlign: "center",
                                  marginBottom: "12px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                }}
                              >
                                Speed: {selectedWaypoint.speed.toFixed(2)} km/h
                              </div>

                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#666",
                                  display: "flex",
                                  gap: "4px",
                                  alignItems: "flex-start",
                                  paddingTop: "8px",
                                  borderTop: "1px solid #e0e0e0",
                                }}
                              >
                                <span
                                  style={{ color: "#ea4335", marginTop: "2px" }}
                                >
                                  📍
                                </span>
                                <div style={{ flex: 1 }}>
                                  {loadingAddress ? (
                                    <span
                                      style={{
                                        color: "#999",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Loading address...
                                    </span>
                                  ) : (
                                    <div
                                      style={{
                                        lineHeight: "1.5",
                                        color: "#333",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {waypointAddresses[
                                        `${selectedWaypoint.latitude.toFixed(6)},${selectedWaypoint.longitude.toFixed(6)}`
                                      ] ||
                                        `Latitude: ${parseFloat(selectedWaypoint.latitude).toFixed(6)}, Longitude: ${parseFloat(selectedWaypoint.longitude).toFixed(6)}`}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </InfoWindow>
                        )}

                        <VehicleMarker
                          position={position}
                          vehicle={vehicle}
                          isMoving={isMoving}
                          heading={heading}
                          pathDirection={pathDirection}
                          onMarkerClick={() =>
                            setShowVehicleDetails((prev) => !prev)
                          }
                          getVehicleIcon={getVehicleIcon}
                          labelOffsetPx={
                            vehicleLabelOffset[vehicle?.device_id ?? "vehicle"]
                              ?.topPx ?? 10
                          }
                          isOverSpeed={isOverSpeed}
                          speedValue={speedValue}
                        />

                        {showVehicleDetails && position && (
                          <VehicleDetailsOverlay
                            key={vehicle?.id || vehicle?.device_id}
                            vehicle={vehicle}
                            route={route}
                            isMoving={isMoving}
                            stoppedDuration={stoppedDuration}
                            position={position}
                          />
                        )}

                        <MapLegend
                          showVehicle
                          vehicleIconUrl={vehicleLegendIconUrl}
                          markerItems={[
                            { label: "Overspeed", color: "#FF0000", symbol: "⚠" },
                          ]}
                          statusItems={
                            roadOffRouteSegments.length > 0
                              ? [{ label: "Deviated Path", color: ACTUAL_PATH_COLOR }]
                              : []
                          }
                          routeColors={route?.routeColor ? [route.routeColor] : []}
                          stopItems={stopLegendItems}
                        />
                      </GoogleMap>

                      <style>{`
                        @keyframes pulse {
                          0% { transform: scale(1); opacity: 1; }
                          50% { transform: scale(1.15); opacity: 0.75; }
                          100% { transform: scale(1); opacity: 1; }
                        }
                      `}</style>
                    </div>

                    {/* ── Stops sidebar ── */}
                    <div
                      style={{
                        flex: "0 0 35%",
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ height: "auto" }}>
                        <StopsTable
                          stops={visibleStops}
                          stopDetails={stopDetails}
                          vehiclePosition={snappedPosition}
                          isMoving={isMoving}
                          stoppedDuration={stoppedDuration}
                          vehicleId={selectedVehicleId}
                          pathDirection={pathDirection}
                          routeCoordinates={route?.routeCoordinates}
                          isInbound={pathDirection === "dest_to_source"}
                          loading={isTrackingDataLoading || !isLoaded}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ── Bottom tabs: Movement Log + Today's Trips ── */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,

                      backgroundColor: "#FFFFFF",
                      borderRadius: "10px",
                      border: "1px solid #A5DCF3",
                      boxShadow: "-2px 1px 5px 1px #00000040",
                    }}
                  >
                    <Tabs
                      value={bottomTab}
                      onChange={(_, newVal) => setBottomTab(newVal)}
                      variant="fullWidth"
                      sx={{
                        mb: 2,
                        "& .MuiTabs-flexContainer": { gap: "0px" },
                        "& .MuiTab-root": {
                          textTransform: "none",
                          fontSize: "16px",
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          minHeight: "48px",
                          border: "1px solid #95A0B3",
                          color: "#666666",
                          backgroundColor: "#FFFFFF",
                          "&.Mui-selected": {
                            backgroundColor: "#20A8E0",
                            color: "#FFFFFF",
                          },
                          "&:first-of-type": {
                            borderTopLeftRadius: "8px",
                            borderBottomLeftRadius: "8px",
                          },
                          "&:last-of-type": {
                            borderTopRightRadius: "8px",
                            borderBottomRightRadius: "8px",
                          },
                          borderRight: "none",
                          "&:last-child": { borderRight: "1px solid #95A0B3" },
                        },
                        "& .MuiTabs-indicator": { display: "none" },
                      }}
                    >
                      <Tab label={t("Current Trip")} />
                      <Tab label={t("Past Trips")} />
                      {isSchoolMonitor && <Tab label={t("Student List")} />}
                      <Tab label={t("Analytics")} />
                    </Tabs>

                    <Box sx={{ display: bottomTab === 0 ? "block" : "none" }}>
                      <TripSegmentTracker
                        vehiclePosition={position}
                        isMoving={isMoving}
                        stoppedDuration={stoppedDuration}
                        vehicleId={vehicle?.device_id}
                        pathDirection={pathDirection}
                        vehicleNumber={
                          vehicle?.VehicleNumber || vehicle?.vehicleNumber
                        }
                        movementLogs={movementLogs}
                        waypoints={waypoints}
                        stops={stops}
                        t={t}
                      />
                    </Box>

                    <Box sx={{ display: bottomTab === 1 ? "block" : "none" }}>
                      <PreviousTripsTable
                        trips={previousTrips}
                        loading={loadingTrips}
                        selectedTrip={selectedHistoricalTrip}
                        onTripClick={fetchTripPath}
                        onClearSelection={handleClearSelectedTrip}
                        loadingPath={loadingTripPath}
                        historicalPath={historicalPath}
                        isLoaded={isLoaded}
                        historyPointInterval={historyPointInterval}
                        vehicle={vehicle}
                        route={route}
                        mapStyles={mapStyles}
                        mapOptions={mapOptions}
                        t={t}
                        tripDate={completedTripsDate}
                        onTripDateChange={(date) => {
                          setCompletedTripsDate(date);
                          handleClearSelectedTrip();
                        }}
                      />
                    </Box>
                    {isSchoolMonitor && (
                      <Box sx={{ display: bottomTab === 2 ? "block" : "none" }}>
                        <StudentListTable
                          vehicleId={vehicle?.id}
                          stops={stops}
                          t={t}
                        />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display:
                          bottomTab === analyticsTabIndex ? "block" : "none",
                      }}
                    >
                      <AnalyticsTable
                        vehicleId={vehicle?.id}
                        deviceId={vehicle?.device_id}
                        vehicle={vehicle}
                        t={t}
                      />
                    </Box>
                  </Box>
                </Box>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DeviceLocationMap;
